import { PDFArray, PDFDocument, PDFName, PDFRawStream, decodePDFRawStream, degrees } from 'pdf-lib';

export function ocrPageNumbers(scope, range, count, pagesWithText = new Set()) {
  if (scope === 'all') return Array.from({ length: count }, (_, i) => i + 1);
  if (scope === 'notext') return Array.from({ length: count }, (_, i) => i + 1).filter((p) => !pagesWithText.has(p));
  const pages = new Set();
  for (const token of range.split(',')) {
    const match = token.trim().match(/^(\d+)(?:\s*-\s*(\d+))?$/);
    const start = Number(match?.[1]);
    const end = Number(match?.[2] || start);
    if (!match || start < 1 || end > count || end < start) throw new Error(`Enter pages from 1 to ${count}, for example 1-3, 5.`);
    for (let page = start; page <= end; page++) pages.add(page);
  }
  return [...pages].sort((a, b) => a - b);
}

export function ocrTextContent(items) {
  return items.map((item) => item.str + (item.hasEOL ? '\n' : ' ')).join('').replace(/ +\n/g, '\n').trim();
}

// Scanned PDF → Office conversion converts the recognized text layers alone.
// LibreOffice's PDF import paints invisible OCR text as visible text, so the
// scan plus its layer double-prints every line; pdf2docx (cloud) drops
// invisible text entirely. With no scan underneath, the text is made visible.
export async function mergeOcrTextLayers(layers) {
  if (!layers.length) throw new Error('OCR did not recognize any pages.');
  const merged = await PDFDocument.create();
  for (const bytes of layers) {
    const layer = await PDFDocument.load(bytes);
    const [page] = await merged.copyPages(layer, [0]);
    merged.addPage(page);
  }
  for (const page of merged.getPages()) {
    const contents = page.node.get(PDFName.of('Contents'));
    const refs = contents instanceof PDFArray ? contents.asArray() : [contents];
    for (const ref of refs) {
      const stream = merged.context.lookup(ref);
      if (!(stream instanceof PDFRawStream)) continue;
      const ops = new TextDecoder('latin1').decode(decodePDFRawStream(stream).decode());
      merged.context.assign(ref, merged.context.flateStream(ops.replace(/(^|\s)3(\s+)Tr\b/g, (_, lead, gap) => `${lead}0${gap}Tr`)));
    }
  }
  return merged.save();
}

// Tesseract builds a proper Unicode, invisible, horizontally fitted text
// layer. Embed that text-only PDF over the original page; never rasterize or
// replace the source, drop unprocessed pages, or return the original as success.
export async function addOcrTextLayers(sourceBytes, results) {
  const doc = await PDFDocument.load(sourceBytes);
  for (const { pageNum, pdfBytes } of results) {
    if (!Number.isInteger(pageNum) || pageNum < 1 || pageNum > doc.getPageCount()) throw new Error('OCR returned an invalid page number.');
    const [textLayer] = await doc.embedPdf(pdfBytes, [0]);
    const page = doc.getPage(pageNum - 1);
    const box = page.getCropBox();
    const angle = ((page.getRotation().angle % 360) + 360) % 360;
    const sideways = angle === 90 || angle === 270;
    page.drawPage(textLayer, {
      x: box.x + (angle === 90 || angle === 180 ? box.width : 0),
      y: box.y + (angle === 180 || angle === 270 ? box.height : 0),
      width: sideways ? box.height : box.width,
      height: sideways ? box.width : box.height,
      rotate: degrees(angle),
    });
  }
  return doc.save();
}
